import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Package, TrendingUp, Clock, AlertCircle, DollarSign, Truck } from 'lucide-react';
import CyberneticLoader from '@/components/CyberneticLoader';
import ExpressBadge from '@/components/ExpressBadge';

const Dashboard = ({ user, onLogout }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get('/stats/dashboard');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const statCards = [
    {
      title: 'Pedidos Totales',
      value: stats?.total_orders || 0,
      icon: Package,
      color: '#224049',
      bgColor: '#E8F5E9'
    },
    {
      title: 'Pedidos Activos',
      value: stats?.active_orders || 0,
      icon: Clock,
      color: '#F57C00',
      bgColor: '#FFF3E0'
    },
    {
      title: 'Pedidos Hoy',
      value: stats?.today_orders || 0,
      icon: TrendingUp,
      color: '#1976D2',
      bgColor: '#E3F2FD'
    },
    {
      title: 'Express',
      value: stats?.express_orders || 0,
      icon: Truck,
      color: '#D4AD6A',
      bgColor: '#FFF9C4'
    },
    {
      title: 'Entregados',
      value: stats?.delivered_orders || 0,
      icon: Package,
      color: '#388E3C',
      bgColor: '#E8F5E9'
    },
    {
      title: 'On-Time Rate',
      value: `${stats?.on_time_rate || 0}%`,
      icon: TrendingUp,
      color: '#388E3C',
      bgColor: '#E8F5E9'
    },
    {
      title: 'Reembolsos',
      value: stats?.total_refunds || 0,
      icon: DollarSign,
      color: '#D32F2F',
      bgColor: '#FFEBEE'
    },
    {
      title: 'Reembolsos Pendientes',
      value: stats?.pending_refunds || 0,
      icon: AlertCircle,
      color: '#F57C00',
      bgColor: '#FFF3E0'
    }
  ];

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-96">
          <CyberneticLoader size={100} message="Cargando dashboard..." />
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout} title="Dashboard">
      <div className="space-y-6" data-testid="dashboard-container">
        {/* Welcome Section - Premium Style */}
        <div className="card-premium p-8" style={{ background: 'linear-gradient(135deg, #224049 0%, #2D5A6B 100%)' }}>
          <h1 className="text-3xl font-bold text-white mb-2" data-testid="welcome-message">
            Bienvenido, {user.name} 👋
          </h1>
          <p className="text-lg font-semibold" style={{ color: '#D4AF37' }}>
            {user.role === 'admin' && '⚡ Panel de Administración UID'}
            {user.role === 'dispensary_owner' && '🏪 Portal del Dispensario'}
            {user.role === 'dispensary_supervisor' && '👔 Supervisor - Dispensario'}
            {user.role === 'dispensary_cashier' && '💰 Cajero - Dispensario'}
            {user.role === 'dispensary_preparer' && '📦 Preparador - Dispensario'}
            {user.role === 'rider' && '🏍️ App de Rider'}
            {user.role === 'support' && '🛠️ Panel de Soporte IT'}
          </p>
          <p className="text-sm mt-2 text-gray-300">
            NO STRESS, UID EXPRESS
          </p>
        </div>

        {/* Stats Grid - Premium Metric Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((stat, index) => {
            const IconComponent = stat.icon;
            return (
              <div
                key={index}
                className="metric-card"
                data-testid={`stat-card-${index}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-gray-600">
                    {stat.title}
                  </h3>
                  <div className="p-3 rounded-full" style={{ backgroundColor: stat.bgColor }}>
                    <IconComponent className="w-6 h-6" style={{ color: stat.color }} />
                  </div>
                </div>
                <div className="text-4xl font-bold" style={{ color: stat.color }}>
                  {stat.value}
                </div>
                {stat.title === 'Express' && (
                  <div className="mt-3">
                    <ExpressBadge express={true} />
                  </div>
                )}
              </div>
              );
            })}
        </div>

        {/* Quick Actions based on role */}
        <div className="card-premium p-6">
          <h2 className="text-xl font-bold mb-6" style={{ color: '#224049' }}>Acciones Rápidas</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {(user.role === 'admin' || user.role === 'dispensary_owner' || user.role === 'dispensary_supervisor' || user.role === 'dispensary_cashier') && (
              <a
                href="/orders/create"
                className="btn-premium p-5 text-center font-semibold text-white hover:shadow-xl"
                style={{ backgroundColor: '#224049' }}
                data-testid="create-order-quick-action"
              >
                📦 Crear Pedido
              </a>
            )}
            <a
              href="/orders"
              className="btn-premium p-5 text-center font-semibold hover:shadow-xl"
              style={{ backgroundColor: '#F0EBE3', color: '#224049' }}
              data-testid="view-orders-quick-action"
            >
              📋 Ver Pedidos
            </a>
            {(user.role === 'admin' || user.role === 'support') && (
              <a
                href="/admin"
                className="btn-premium p-5 text-center font-semibold text-white hover:shadow-xl"
                style={{ background: 'linear-gradient(135deg, #D4AF37 0%, #F4D03F 100%)' }}
                data-testid="admin-panel-quick-action"
                >
                  ⚡ Panel Admin
                </a>
              )}
            {['admin', 'support', 'dispensary_owner', 'dispensary_supervisor'].includes(user.role) && (
              <a
                href="/analytics"
                className="btn-premium p-5 text-center font-semibold text-white hover:shadow-xl"
                style={{ background: 'linear-gradient(135deg, #0F766E 0%, #14B8A6 100%)' }}
                data-testid="analytics-quick-action"
              >
                📊 Analítica Avanzada
              </a>
            )}
            </div>
          </div>
      </div>
    </Layout>
  );
};

export default Dashboard;